import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workspaceAPI, documentAPI, sourceAPI, companyAPI } from '../api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, FolderOpen, LogOut, FileText, Clock, ChevronRight, Search,
  Upload, Globe, FileType, Loader2, X, CheckCircle2, XCircle, Users, ChevronDown, ChevronUp,
  LayoutGrid, List, Settings, Trash2
} from 'lucide-react';

const WS_EMOJI_OPTIONS = [
  '\u{1F4DA}', '\u2696\ufe0f', '\u{1F4B0}', '\u{1F4CA}', '\u{1F465}', '\u{1F4BB}',
  '\u{1F52C}', '\u{1F6E1}\ufe0f', '\u{1F4E3}', '\u{1F4CB}', '\u{1F3AF}', '\u{1F393}',
  '\u{1F4C4}', '\u{1FA7A}', '\u{1F4C8}', '\u2699\ufe0f', '\u{1F3A8}', '\u{1F4EC}',
];

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [canViewSettings, setCanViewSettings] = useState(true);

  const [filterTag, setFilterTag] = useState('');
  const [groupByTag, setGroupByTag] = useState(false);

  useEffect(() => { loadWorkspaces(); loadPermissions(); }, []);
  useEffect(() => { document.title = 'Dashboard | BrainShift AI Workspace'; }, []);

  const loadPermissions = async () => {
    try {
      const res = await companyAPI.myPermissions();
      setCanViewSettings(res.data.permissions?.can_view_settings !== false);
    } catch { setCanViewSettings(true); }
  };

  const loadWorkspaces = async () => {
    try {
      const cached = sessionStorage.getItem('bkw_workspaces');
      if (cached) {
        setWorkspaces(JSON.parse(cached));
        setLoading(false);
      }
    } catch (e) { console.error('Cache parse error:', e); }
    try {
      const res = await workspaceAPI.list();
      setWorkspaces(res.data);
      try { sessionStorage.setItem('bkw_workspaces', JSON.stringify(res.data)); } catch (e) { console.error('Cache write error:', e); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Collect all unique tags
  const allTags = useMemo(() => [...new Set(workspaces.flatMap(w => w.tags || []))].sort(), [workspaces]);

  const filtered = useMemo(() => workspaces.filter(w => {
    const matchesSearch = !search || w.name.toLowerCase().includes(search.toLowerCase()) || w.description?.toLowerCase().includes(search.toLowerCase());
    const matchesTag = !filterTag || (w.tags || []).includes(filterTag);
    return matchesSearch && matchesTag;
  }), [workspaces, search, filterTag]);

  // Group by tag — memoised so the sort+map+entries chain doesn't re-run on every render
  // (e.g. typing in unrelated input fields). Only recomputes when the filtered list or
  // the groupByTag toggle change.
  const groupedEntries = useMemo(() => {
    if (!groupByTag) return [];
    const buckets = {};
    for (const ws of filtered) {
      const tags = (ws.tags || []).length > 0 ? ws.tags : ['Untagged'];
      for (const tag of tags) {
        if (!buckets[tag]) buckets[tag] = [];
        buckets[tag].push(ws);
      }
    }
    return Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered, groupByTag]);

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <header className="border-b border-[#E5E7EB] bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brainshift-logo.png" alt="BrainShift" className="w-auto" style={{ height: '2rem' }} />
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#4B5563] border border-[#E5E7EB] px-2 py-0.5 rounded-lg">Workspace</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#4B5563]">{user?.first_name} {user?.last_name}</span>
            <span className="text-xs bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5 rounded-lg font-semibold text-[#4B5563] uppercase tracking-wider">{user?.role}</span>
            {canViewSettings && (
              <button onClick={() => navigate('/settings')} className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors" data-testid="settings-nav-btn" title="Settings">
                <Settings className="w-4 h-4 text-[#4B5563]" />
              </button>
            )}
            {user?.is_super_admin && (
              <button onClick={() => navigate('/superadmin')} className="h-7 px-2.5 bg-[#111827] text-white text-[10px] font-bold uppercase tracking-wider rounded-lg hover:bg-[#1F2937] transition-colors" data-testid="superadmin-nav-btn">
                Super Admin
              </button>
            )}
            <button onClick={() => { logout(); navigate('/login'); }} className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4 text-[#4B5563]" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#111827] mb-1" style={{ fontFamily: 'var(--font-heading)' }}>AI Workspaces</h1>
            <p className="text-[#4B5563]">{loading ? 'Loading...' : `${workspaces.length} workspace${workspaces.length !== 1 ? 's' : ''} available`}</p>
          </div>
          <button onClick={() => setDialogOpen(true)} className="h-10 px-5 bg-[#6590FF] hover:bg-[#4B7CFF] text-white font-semibold text-sm flex items-center gap-2 rounded-xl transition-colors" data-testid="create-workspace-btn">
            <Plus className="w-4 h-4" /> New AI Workspace
          </button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-11 rounded-xl border-[#E5E7EB] text-base" placeholder="Search workspaces..." data-testid="search-workspaces" />
          </div>
          <button onClick={() => setGroupByTag(!groupByTag)}
            className={`h-11 px-4 text-sm font-semibold rounded-xl border transition-colors flex items-center gap-1.5 ${groupByTag ? 'bg-[#6590FF]/5 border-[#6590FF]/30 text-[#6590FF]' : 'border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]'}`}
            data-testid="group-by-tag-btn" title="Group by tag">
            <LayoutGrid className="w-4 h-4" /> Group
          </button>
          <div className="flex bg-[#F3F4F6] p-0.5 rounded-lg" data-testid="view-mode-toggle">
            <button onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-[#111827]' : 'text-[#9CA3AF] hover:text-[#4B5563]'}`}
              data-testid="view-list-btn" title="List view"><List className="w-4 h-4" /></button>
            <button onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-[#111827]' : 'text-[#9CA3AF] hover:text-[#4B5563]'}`}
              data-testid="view-grid-btn" title="Grid view"><LayoutGrid className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Tag Filter Bar */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="tag-filter-bar">
            <button onClick={() => setFilterTag('')}
              className={`h-8 px-3.5 text-sm font-semibold rounded-full border transition-colors ${!filterTag ? 'bg-[#6590FF] text-white border-[#6590FF]' : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#6590FF] hover:text-[#6590FF]'}`}
              data-testid="tag-filter-all">All</button>
            {allTags.map(tag => (
              <button key={tag} onClick={() => setFilterTag(filterTag === tag ? '' : tag)}
                className={`h-8 px-3.5 text-sm font-semibold rounded-full border transition-colors ${filterTag === tag ? 'bg-[#6590FF] text-white border-[#6590FF]' : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#6590FF] hover:text-[#6590FF]'}`}
                data-testid={`tag-filter-${tag}`}>{tag}</button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center py-16 gap-3">
            <div className="flex gap-1">
              <div className="w-2 h-2 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-sm text-[#4B5563]">Loading workspaces...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-16 text-center rounded-2xl">
            <FolderOpen className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#111827] mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{search || filterTag ? 'No matching workspaces' : 'No workspaces yet'}</h3>
            <p className="text-sm text-[#4B5563] mb-4">{search || filterTag ? 'Try a different search or filter' : 'Create your first AI workspace to get started'}</p>
          </div>
        ) : groupByTag ? (
          /* Grouped by Tag View */
          <div className="space-y-6" data-testid="workspaces-grouped-view">
            {groupedEntries.map(([tag, wsList]) => (
              <div key={tag}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#6590FF] bg-[#6590FF]/5 px-2.5 py-1 border border-[#6590FF]/20 rounded-full">{tag}</span>
                  <span className="text-xs text-[#9CA3AF]">{wsList.length}</span>
                  <div className="flex-1 h-px bg-[#E5E7EB]" />
                </div>
                <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "border border-[#E5E7EB] rounded-2xl overflow-hidden bg-white divide-y divide-[#F3F4F6]"}>
                  {wsList.map(ws => viewMode === 'grid' ? (
                    <WorkspaceGridCard key={ws.id} ws={ws} onNavigate={() => navigate(`/workspace/${ws.id}`)} onIconChange={loadWorkspaces} onTagsChange={loadWorkspaces} onDeleted={loadWorkspaces} />
                  ) : (
                    <WorkspaceListItem key={ws.id} ws={ws} onNavigate={() => navigate(`/workspace/${ws.id}`)} onDeleted={loadWorkspaces} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden bg-white divide-y divide-[#F3F4F6]" data-testid="workspaces-list-view">
            {filtered.map((ws) => (
              <WorkspaceListItem key={ws.id} ws={ws} onNavigate={() => navigate(`/workspace/${ws.id}`)} onDeleted={loadWorkspaces} />
            ))}
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="workspaces-grid-view">
            {filtered.map((ws) => (
              <WorkspaceGridCard key={ws.id} ws={ws} onNavigate={() => navigate(`/workspace/${ws.id}`)} onIconChange={loadWorkspaces} onTagsChange={loadWorkspaces} onDeleted={loadWorkspaces} />
            ))}
          </div>
        )}
      </main>

      {dialogOpen && <CreateWorkspaceDialog onClose={() => setDialogOpen(false)} onCreated={(id) => navigate(`/workspace/${id}`)} />}
    </div>
  );
}

function CreateWorkspaceDialog({ onClose, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [newWs, setNewWs] = useState({ name: '', description: '', category: 'General' });
  const [wsTags, setWsTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const tagSuggestions = ['Compliance', 'Legal', 'Finance', 'HR', 'Technical', 'Research', 'Strategy', 'Marketing'];

  // Sources state (optional, staged before creation)
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [sourceTab, setSourceTab] = useState('upload');
  const [stagedFiles, setStagedFiles] = useState([]);
  const [webUrls, setWebUrls] = useState('');
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [stagedTexts, setStagedTexts] = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setStagedFiles(prev => [...prev, ...files]);
      setSourcesExpanded(true);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeStagedFile = (idx) => setStagedFiles(prev => prev.filter((_, i) => i !== idx));

  const addStagedText = () => {
    if (!textTitle.trim() || !textContent.trim()) return;
    setStagedTexts(prev => [...prev, { title: textTitle, content: textContent }]);
    setTextTitle('');
    setTextContent('');
  };

  const removeStagedText = (idx) => setStagedTexts(prev => prev.filter((_, i) => i !== idx));

  const totalStagedSources = stagedFiles.length + (webUrls.split('\n').filter(u => u.trim()).length) + stagedTexts.length;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newWs.name.trim()) return;
    setCreating(true);
    try {
      const res = await workspaceAPI.create({ ...newWs, tags: wsTags });
      const wsId = res.data.id;
      // Invalidate workspace list cache
      try { sessionStorage.removeItem('bkw_workspaces'); } catch (e) { console.error('Cache clear error:', e); }
      const results = [];

      // Upload staged files
      for (const file of stagedFiles) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          await documentAPI.upload(wsId, fd);
          results.push({ label: file.name, status: 'ok' });
        } catch { results.push({ label: file.name, status: 'error' }); }
      }

      // Add websites
      const urls = webUrls.split('\n').map(u => u.trim()).filter(Boolean);
      if (urls.length) {
        try {
          await sourceAPI.addWebBatch(wsId, urls);
          urls.forEach(u => results.push({ label: u, status: 'ok' }));
        } catch { urls.forEach(u => results.push({ label: u, status: 'error' })); }
      }

      // Add text sources
      for (const txt of stagedTexts) {
        try {
          await sourceAPI.addText(wsId, { title: txt.title, content: txt.content });
          results.push({ label: txt.title, status: 'ok' });
        } catch { results.push({ label: txt.title, status: 'error' }); }
      }

      setUploadProgress(results);
      // Brief delay to show results then navigate
      setTimeout(() => onCreated(wsId), results.length ? 800 : 0);
    } catch (err) { console.error(err); setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="create-workspace-overlay">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="create-workspace-dialog">
        <div className="h-14 border-b border-[#E5E7EB] flex items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <img src="/brainshift-logo.png" alt="BrainShift" className="w-auto" style={{ height: '1.5rem' }} />
            <span className="text-base font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>New AI Workspace</span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors"><X className="w-4 h-4 text-[#4B5563]" /></button>
        </div>

        <form onSubmit={handleCreate} className="p-6 max-h-[75vh] overflow-y-auto space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">Workspace Name</Label>
            <Input value={newWs.name} onChange={(e) => setNewWs({ ...newWs, name: e.target.value })} required className="rounded-xl border-[#E5E7EB] h-11" placeholder="e.g., Q4 Compliance Review" data-testid="workspace-name-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">Description</Label>
            <Textarea value={newWs.description} onChange={(e) => setNewWs({ ...newWs, description: e.target.value })} className="rounded-xl border-[#E5E7EB] resize-none" rows={2} placeholder="Describe the purpose" data-testid="workspace-description-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2 block">Tags</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {wsTags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 text-xs font-semibold text-[#6590FF] bg-[#6590FF]/5 px-2.5 py-1 border border-[#6590FF]/20 rounded-full">
                  {tag}
                  <button type="button" onClick={() => setWsTags(prev => prev.filter(t => t !== tag))} className="hover:text-[#C8102E]"><X className="w-3 h-3" /></button>
                </span>
              ))}
              <div className="flex items-center gap-1">
                <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (tagInput.trim() && !wsTags.includes(tagInput.trim())) { setWsTags(prev => [...prev, tagInput.trim()]); setTagInput(''); } } }}
                  placeholder="Add tag..."
                  className="h-7 px-2 text-xs border border-[#E5E7EB] rounded-lg w-24 focus:outline-none focus:border-[#6590FF] focus:w-32 transition-all"
                  data-testid="create-ws-tag-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tagSuggestions.filter(s => !wsTags.includes(s)).slice(0, 6).map(s => (
                <button key={s} type="button" onClick={() => setWsTags(prev => [...prev, s])}
                  className="text-[10px] font-medium text-[#9CA3AF] hover:text-[#6590FF] px-2 py-0.5 border border-dashed border-[#D1D5DB] hover:border-[#6590FF] rounded-full transition-colors">
                  + {s}
                </button>
              ))}
            </div>
          </div>

          {/* Optional Sources Section */}
          <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden">
            <button type="button" onClick={() => setSourcesExpanded(!sourcesExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm text-[#4B5563] hover:bg-[#F9FAFB] transition-colors" data-testid="toggle-sources-section">
              <span className="flex items-center gap-2 font-medium">
                <Upload className="w-4 h-4" />
                Add Sources
                <span className="text-[10px] text-[#9CA3AF] font-normal">(optional)</span>
                {totalStagedSources > 0 && <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#6590FF] text-white text-[10px] font-bold">{totalStagedSources}</span>}
              </span>
              {sourcesExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {sourcesExpanded && (
              <div className="border-t border-[#E5E7EB] p-4">
                <SourceTabBar sourceTab={sourceTab} setSourceTab={setSourceTab} />
                {sourceTab === 'upload' && (
                  <div className="space-y-3">
                    <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.txt,.pptx,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tiff,.tif" className="hidden" onChange={handleFileSelect} />
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                      className="w-full h-24 border-2 border-dashed border-[#D1D5DB] hover:border-[#6590FF] rounded-xl flex flex-col items-center justify-center gap-1.5 transition-colors" data-testid="upload-drop-zone">
                      <Upload className="w-5 h-5 text-[#9CA3AF]" />
                      <span className="text-xs text-[#4B5563]">Drop files or click to upload</span>
                      <span className="text-[10px] text-[#9CA3AF]">PDF, DOCX, PPTX, XLSX, CSV, TXT, Images</span>
                    </button>
                    {stagedFiles.length > 0 && (
                      <div className="space-y-1.5">
                        {stagedFiles.map((f, i) => (
                          <div key={`staged-file-${f.name}-${f.size}`} className="flex items-center gap-2 py-1.5 px-3 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB]">
                            <FileText className="w-3.5 h-3.5 text-[#6590FF] flex-shrink-0" />
                            <span className="text-xs text-[#111827] truncate flex-1">{f.name}</span>
                            <button type="button" onClick={() => removeStagedFile(i)} className="p-0.5 hover:bg-[#E5E7EB] rounded-lg"><X className="w-3 h-3 text-[#9CA3AF]" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {sourceTab === 'web' && (
                  <div className="space-y-3">
                    <Label className="text-xs font-medium text-[#4B5563] mb-1 block">Website URLs (one per line)</Label>
                    <Textarea value={webUrls} onChange={e => setWebUrls(e.target.value)} placeholder={"https://example.com/page1\nhttps://example.com/page2"} rows={4} className="rounded-xl resize-none font-mono text-xs" data-testid="web-urls-textarea" />
                    {webUrls.split('\n').filter(u => u.trim()).length > 0 && (
                      <p className="text-[10px] text-[#6B7280]">{webUrls.split('\n').filter(u => u.trim()).length} URL(s) will be fetched on creation</p>
                    )}
                  </div>
                )}
                {sourceTab === 'text' && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs font-medium text-[#4B5563] mb-1 block">Title</Label>
                      <Input value={textTitle} onChange={e => setTextTitle(e.target.value)} placeholder="Note title" className="rounded-xl h-9" data-testid="text-title-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-[#4B5563] mb-1 block">Content</Label>
                      <Textarea value={textContent} onChange={e => setTextContent(e.target.value)} placeholder="Paste or type your text here..." rows={4} className="rounded-xl resize-none" data-testid="text-content-input" />
                    </div>
                    <button type="button" onClick={addStagedText} disabled={!textTitle.trim() || !textContent.trim()}
                      className="h-8 px-4 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#4B5563] text-xs font-semibold rounded-xl transition-colors disabled:opacity-40 flex items-center gap-1.5" data-testid="stage-text-btn">
                      <Plus className="w-3 h-3" /> Add to queue
                    </button>
                    {stagedTexts.length > 0 && (
                      <div className="space-y-1.5">
                        {stagedTexts.map((t, i) => (
                          <div key={`staged-text-${t.title}-${i}`} className="flex items-center gap-2 py-1.5 px-3 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB]">
                            <FileType className="w-3.5 h-3.5 text-[#6590FF] flex-shrink-0" />
                            <span className="text-xs text-[#111827] truncate flex-1">{t.title}</span>
                            <button type="button" onClick={() => removeStagedText(i)} className="p-0.5 hover:bg-[#E5E7EB] rounded-lg"><X className="w-3 h-3 text-[#9CA3AF]" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Upload progress results */}
          {uploadProgress.length > 0 && <ResultList items={uploadProgress} />}

          <button type="submit" disabled={creating || !newWs.name.trim()} className="w-full h-11 bg-[#6590FF] hover:bg-[#4B7CFF] text-white font-semibold text-sm rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="workspace-create-submit">
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <>Create Workspace {totalStagedSources > 0 && `& Add ${totalStagedSources} Source${totalStagedSources > 1 ? 's' : ''}`}</>}
          </button>
          <button type="button" onClick={onClose} className="w-full h-9 text-sm text-[#4B5563] hover:text-[#111827] transition-colors rounded-xl">Cancel</button>
        </form>
      </div>
    </div>
  );
}

function SourceTabBar({ sourceTab, setSourceTab }) {
  const tabs = [
    { key: 'upload', icon: Upload, label: 'Upload Files' },
    { key: 'web', icon: Globe, label: 'Websites' },
    { key: 'text', icon: FileType, label: 'Copied Text' }
  ];
  return (
    <div className="flex gap-1 border-b border-[#E5E7EB] mb-4">
      {tabs.map(t => (
        <button key={t.key} onClick={() => setSourceTab(t.key)}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${sourceTab === t.key ? 'border-[#6590FF] text-[#6590FF]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'}`} data-testid={`create-source-tab-${t.key}`}>
          <t.icon className="w-3.5 h-3.5" />{t.label}
        </button>
      ))}
    </div>
  );
}

function ResultList({ items }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1.5 mt-2">
      {items.map((item, i) => (
        <div key={item.label ? `${item.label}-${i}` : `result-${i}`} className="flex items-center gap-2 py-1.5 px-3 bg-[#F9FAFB] rounded-lg border border-[#E5E7EB]">
          {item.status === 'ok' || item.status === 'ready' ? <CheckCircle2 className="w-3.5 h-3.5 text-[#059669] flex-shrink-0" /> :
           item.status === 'error' ? <XCircle className="w-3.5 h-3.5 text-[#DC2626] flex-shrink-0" /> :
           <Loader2 className="w-3.5 h-3.5 text-[#6590FF] animate-spin flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#111827] truncate">{item.label}</p>
            {item.sublabel && <p className="text-[10px] text-[#6B7280] truncate">{item.sublabel}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}


function WorkspaceGridCard({ ws, onNavigate, onIconChange, onTagsChange, onDeleted }) {
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const changeIcon = async (newIcon) => {
    setUpdating(true);
    setIconPickerOpen(false);
    try {
      await workspaceAPI.update(ws.id, { icon: newIcon });
      onIconChange();
    } catch (e) { console.error(e); }
    setUpdating(false);
  };

  const addTag = async () => {
    if (!tagInput.trim()) return;
    try {
      await workspaceAPI.addTag(ws.id, tagInput.trim());
      setTagInput('');
      setShowTagInput(false);
      onTagsChange();
    } catch (e) { console.error(e); }
  };

  const removeTag = async (tag) => {
    try {
      await workspaceAPI.removeTag(ws.id, tag);
      onTagsChange();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await workspaceAPI.delete(ws.id);
      setConfirmDeleteOpen(false);
      onDeleted?.();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to delete workspace');
    } finally {
      setDeleting(false);
    }
  };

  const wsIcon = ws.icon || '\u{1F4DA}';

  return (
    <div className="relative text-left border border-[#E5E7EB] bg-white p-5 hover:border-[#6590FF] transition-colors group rounded-xl" data-testid={`workspace-card-${ws.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setIconPickerOpen(!iconPickerOpen); }}
            className="w-11 h-11 rounded-xl bg-[#F3F4F6] border border-[#E5E7EB] flex items-center justify-center hover:bg-[#E5E7EB] transition-colors text-2xl"
            data-testid={`workspace-icon-${ws.id}`} title="Change icon">
            {updating ? <Loader2 className="w-5 h-5 animate-spin text-[#6590FF]" /> : wsIcon}
          </button>
          {/* Tags */}
          <div className="flex flex-wrap gap-1">
            {(ws.tags || []).map(tag => (
              <span key={tag} className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#6590FF] bg-[#6590FF]/5 px-2 py-0.5 border border-[#6590FF]/20 rounded-full">
                {tag}
                <button onClick={(e) => { e.stopPropagation(); removeTag(tag); }} className="hover:text-[#C8102E] ml-0.5" data-testid={`remove-tag-${tag}`}>
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
            <button onClick={(e) => { e.stopPropagation(); setShowTagInput(true); }}
              className="text-xs font-medium text-[#9CA3AF] hover:text-[#6590FF] px-1.5 py-0.5 border border-dashed border-[#D1D5DB] hover:border-[#6590FF] rounded-full transition-colors"
              data-testid={`add-tag-btn-${ws.id}`}>+ tag</button>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onDeleted && (
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(true); }}
              className="p-1.5 rounded-lg hover:bg-[#FEE2E2] opacity-0 group-hover:opacity-100 transition-all"
              data-testid={`workspace-delete-btn-${ws.id}`}
              title="Delete workspace"
              aria-label="Delete workspace"
            >
              <Trash2 className="w-4 h-4 text-[#9CA3AF] hover:text-[#DC2626] transition-colors" />
            </button>
          )}
          <ChevronRight className="w-4 h-4 text-[#D1D5DB] group-hover:text-[#6590FF] transition-colors cursor-pointer" onClick={onNavigate} />
        </div>
      </div>

      {/* Tag Input */}
      {showTagInput && (
        <div className="mb-3 flex items-center gap-1.5" onClick={e => e.stopPropagation()} data-testid={`tag-input-${ws.id}`}>
          <input value={tagInput} onChange={e => setTagInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addTag(); if (e.key === 'Escape') setShowTagInput(false); }}
            placeholder="Enter tag..."
            className="h-7 px-2 text-sm border border-[#E5E7EB] rounded-lg flex-1 focus:outline-none focus:border-[#6590FF]"
            autoFocus data-testid={`tag-input-field-${ws.id}`} />
          <button onClick={addTag} className="h-7 px-2 bg-[#6590FF] text-white text-xs font-semibold rounded-lg">Add</button>
          <button onClick={() => setShowTagInput(false)} className="h-7 px-1.5 text-[#9CA3AF] hover:text-[#111827]"><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* Emoji Picker */}
      {iconPickerOpen && (
        <div className="absolute top-16 left-5 z-50 bg-white border border-[#E5E7EB] rounded-xl shadow-xl p-3 w-[240px]" onClick={e => e.stopPropagation()} data-testid={`icon-picker-${ws.id}`}>
          <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wider mb-2">Choose Icon</p>
          <div className="grid grid-cols-6 gap-1.5">
            {WS_EMOJI_OPTIONS.map(em => (
              <button key={em} onClick={() => changeIcon(em)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg transition-colors ${wsIcon === em ? 'bg-[#6590FF]/10 ring-2 ring-[#6590FF]' : 'hover:bg-[#F3F4F6]'}`}
                data-testid={`ws-emoji-option`}>
                {em}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="cursor-pointer" onClick={onNavigate}>
        <h3 className="text-xl font-semibold text-[#111827] mb-1.5 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{ws.name}</h3>
        <p className="text-base text-[#4B5563] line-clamp-2 mb-4">{ws.description || 'No description'}</p>
        <div className="flex items-center gap-4 text-sm text-[#4B5563]">
          <span className="flex items-center gap-1.5"><FileText className="w-4 h-4" />{ws.doc_count} doc{ws.doc_count !== 1 ? 's' : ''}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{new Date(ws.date_modified).toLocaleDateString()}</span>
          {ws.is_shared && <span className="text-xs text-[#D97706] flex items-center gap-0.5"><Users className="w-3 h-3" />{ws.share_role}</span>}
        </div>
      </div>

      {confirmDeleteOpen && (
        <DeleteWorkspaceConfirm
          ws={ws}
          deleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => !deleting && setConfirmDeleteOpen(false)}
        />
      )}
    </div>
  );
}

// Custom delete-confirmation popup. Shown only when the user clicks the trash icon
// on a workspace card. Mentions the destructive scope explicitly per the user's UX ask.
function DeleteWorkspaceConfirm({ ws, deleting, onConfirm, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="delete-workspace-overlay"
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150"
        onClick={e => e.stopPropagation()}
        data-testid="delete-workspace-dialog"
      >
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#FEE2E2] flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-[#DC2626]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-[#111827] tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                Delete this workspace?
              </h3>
              <p className="text-sm text-[#4B5563] mt-1.5 leading-relaxed">
                Are you sure you want to delete <span className="font-semibold text-[#111827]">{ws.name}</span>?
                This will permanently delete all workspace chats, sources, summaries, and AI history. This action cannot be undone.
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="h-9 px-4 text-sm font-medium text-[#4B5563] hover:bg-[#E5E7EB] rounded-xl transition-colors disabled:opacity-50"
            data-testid="delete-workspace-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="h-9 px-4 text-sm font-semibold text-white bg-[#DC2626] hover:bg-[#B91C1C] rounded-xl transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="delete-workspace-confirm"
          >
            {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Yes, delete workspace
          </button>
        </div>
      </div>
    </div>
  );
}

function WorkspaceListItem({ ws, onNavigate, onDeleted }) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const wsIcon = ws.icon || '\u{1F4DA}';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await workspaceAPI.delete(ws.id);
      setConfirmDeleteOpen(false);
      onDeleted?.();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Failed to delete workspace');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="w-full text-left px-5 py-4 hover:bg-[#F8FAFC] transition-colors group flex items-center gap-4" data-testid={`workspace-card-${ws.id}`}>
        <button onClick={onNavigate} className="flex items-center gap-4 flex-1 min-w-0 text-left">
          <div className="w-10 h-10 rounded-xl bg-[#F3F4F6] flex items-center justify-center flex-shrink-0 text-xl">
            {wsIcon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-[#111827] truncate" style={{ fontFamily: 'var(--font-heading)' }}>{ws.name}</h3>
              {(ws.tags || []).map(tag => (
                <span key={tag} className="text-xs font-bold text-[#6590FF] bg-[#6590FF]/5 px-1.5 py-0.5 rounded-md flex-shrink-0">{tag}</span>
              ))}
              {ws.is_shared && <span className="text-xs font-bold text-[#D97706] bg-[#FEF3C7] px-1.5 py-0.5 rounded-md flex-shrink-0 flex items-center gap-0.5"><Users className="w-3 h-3" />{ws.share_role}</span>}
            </div>
            <p className="text-sm text-[#6B7280] truncate mt-0.5">{ws.description || 'No description'}</p>
          </div>
          <div className="flex items-center gap-4 text-sm text-[#6B7280] flex-shrink-0 hidden sm:flex">
            <span className="flex items-center gap-1.5"><FileText className="w-4 h-4" />{ws.doc_count}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{new Date(ws.date_modified).toLocaleDateString()}</span>
          </div>
        </button>
        {onDeleted && (
          <button
            onClick={() => setConfirmDeleteOpen(true)}
            className="p-1.5 rounded-lg hover:bg-[#FEE2E2] opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
            data-testid={`workspace-delete-btn-${ws.id}`}
            title="Delete workspace"
            aria-label="Delete workspace"
          >
            <Trash2 className="w-4 h-4 text-[#9CA3AF] hover:text-[#DC2626] transition-colors" />
          </button>
        )}
        <ChevronRight className="w-4 h-4 text-[#D1D5DB] group-hover:text-[#6590FF] transition-colors flex-shrink-0" />
      </div>
      {confirmDeleteOpen && (
        <DeleteWorkspaceConfirm
          ws={ws}
          deleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => !deleting && setConfirmDeleteOpen(false)}
        />
      )}
    </>
  );
}
